import React, { useState } from 'react';
import { api } from '../services/api';
import ConfirmModal from '../components/ConfirmModal';
import { isMainList, isSpecialList, isTomorrowList, isDailyHabitsList } from '../constants/listTypes';

export default function ListManagement({ user, lists, onListsChange }) {
  const [deleteModal, setDeleteModal] = useState({ isOpen: false, list: null });
  const [editingListId, setEditingListId] = useState(null);
  const [editingListName, setEditingListName] = useState('');
  const [draggingListId, setDraggingListId] = useState(null);
  const [dragOverListId, setDragOverListId] = useState(null);

  const handleDeleteClick = (list) => {
    // Only user_list and dailyhabits types can be deleted (not main or tomorrow)
    if (isMainList(list.list_type) || isTomorrowList(list.list_type)) {
      return;
    }
    setDeleteModal({ isOpen: true, list });
  };

  const handleDeleteConfirm = async () => {
    if (!deleteModal.list) return;

    try {
      await api.deleteList(deleteModal.list.id);
      onListsChange(lists.filter(list => list.id !== deleteModal.list.id));
      setDeleteModal({ isOpen: false, list: null });
    } catch (error) {
      console.error('Error deleting list:', error);
      alert('Failed to delete list. Please try again.');
    }
  };

  const handleDeleteCancel = () => {
    setDeleteModal({ isOpen: false, list: null });
  };

  const handleEditClick = (list) => {
    if (isMainList(list.list_type) || isTomorrowList(list.list_type) || isDailyHabitsList(list.list_type)) {
      return;
    }
    setEditingListId(list.id);
    setEditingListName(list.name);
  };

  const handleEditCancel = () => {
    setEditingListId(null);
    setEditingListName('');
  };

  const handleEditSave = async (listId) => {
    const trimmedName = editingListName.trim();
    if (!trimmedName) {
      alert('List name cannot be empty');
      return;
    }

    try {
      await api.updateList(listId, trimmedName);
      // Update the list in state
      onListsChange(lists.map(list => 
        list.id === listId ? { ...list, name: trimmedName } : list
      ));
      setEditingListId(null);
      setEditingListName('');
    } catch (error) {
      console.error('Error updating list name:', error);
      alert('Failed to update list name. Please try again.');
    }
  };

  const handleEditKeyDown = (e, listId) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleEditSave(listId);
    } else if (e.key === 'Escape') {
      handleEditCancel();
    }
  };

  // Drag and drop handlers for reordering lists
  const handleListDragStart = (e, listId) => {
    // Only allow dragging user_list types (not main or special lists)
    const list = lists.find(l => l.id === listId);
    if (!list || isSpecialList(list.list_type)) {
      e.preventDefault();
      return;
    }
    // Prevent dragging while editing
    if (editingListId === listId) {
      e.preventDefault();
      return;
    }
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', listId);
    setDraggingListId(listId);
  };

  const handleListDragOver = (e) => {
    e.preventDefault();
  };

  const handleListDragEnter = (listId) => {
    if (!draggingListId || draggingListId === listId) return;
    const list = lists.find(l => l.id === listId);
    // Only allow dropping on user_list types
    if (list && !isSpecialList(list.list_type)) {
      setDragOverListId(listId);
    }
  };

  const handleListDragEnd = () => {
    setDraggingListId(null);
    setDragOverListId(null);
  };

  const handleListDrop = async (e, targetListId) => {
    e.preventDefault();
    const draggedListId = e.dataTransfer.getData('text/plain');
    if (!draggedListId || draggedListId === targetListId) {
      setDraggingListId(null);
      setDragOverListId(null);
      return;
    }

    const draggedList = lists.find(l => l.id === draggedListId);
    const targetList = lists.find(l => l.id === targetListId);
    
    // Only allow reordering user_list types
    if (!draggedList || !targetList || isSpecialList(draggedList.list_type) || isSpecialList(targetList.list_type)) {
      setDraggingListId(null);
      setDragOverListId(null);
      return;
    }

    // Reorder lists in local state
    const reorderedLists = Array.from(lists);
    const fromIndex = reorderedLists.findIndex(list => list.id === draggedListId);
    const toIndex = reorderedLists.findIndex(list => list.id === targetListId);

    if (fromIndex === -1 || toIndex === -1 || fromIndex === toIndex) {
      setDraggingListId(null);
      setDragOverListId(null);
      return;
    }

    const [moved] = reorderedLists.splice(fromIndex, 1);
    reorderedLists.splice(toIndex, 0, moved);

    // Optimistically update state
    onListsChange(reorderedLists);
    setDraggingListId(null);
    setDragOverListId(null);

    // Call API to persist the new order
    const orderedListIds = reorderedLists.map(list => list.id);
    try {
      await api.reorderLists(orderedListIds);
    } catch (error) {
      console.error('Error reordering lists:', error);
      // Reload lists on error to restore correct order
      try {
        const data = await api.getLists();
        onListsChange(data);
      } catch (reloadError) {
        console.error('Error reloading lists after reorder failure:', reloadError);
      }
    }
  };

  return (
    <>
      {user && (
        <div className="account-user-info">
          <p className="account-user-name">{user.name}</p>
          <p className="account-user-email">{user.email}</p>
        </div>
      )}

      {lists.length === 0 ? (
        <div className="empty-state">
          <div className="empty-state-icon">📝</div>
          <p className="empty-state-text">No lists found</p>
        </div>
      ) : (
        <div className="account-lists">
          {lists.map(list => {
            const isMain = isMainList(list.list_type);
            const isTomorrow = isTomorrowList(list.list_type);
            const isDailyHabits = isDailyHabitsList(list.list_type);
            const isSpecial = isSpecialList(list.list_type);
            // Prefer the count fields from the lightweight metadata endpoint; fall
            // back to counting a tasks array if a full-list object is ever passed in.
            const itemCount = list.item_count ?? (list.tasks ? list.tasks.length : 0);
            const completedCount = list.completed_count ?? (list.tasks ? list.tasks.filter(task => task.completed).length : 0);
            const openCount = itemCount - completedCount;
            const displayName = isTomorrow ? 'To Do Tomorrow' : list.name;

            const isEditing = editingListId === list.id;
            const canDrag = !isSpecial && !isEditing;
            const showDropIndicator = draggingListId && dragOverListId === list.id && canDrag;

            return (
              <React.Fragment key={list.id}>
                {showDropIndicator && (
                  <div className="todo-drop-indicator" />
                )}
                <div
                  className={`account-list-item ${isMain ? 'account-list-item-main' : ''} ${isTomorrow ? 'account-list-item-tomorrow' : ''} ${draggingListId === list.id ? 'todo-item-dragging' : ''}`}
                  draggable={canDrag}
                  onDragStart={(e) => handleListDragStart(e, list.id)}
                  onDragOver={handleListDragOver}
                  onDragEnter={() => handleListDragEnter(list.id)}
                  onDragEnd={handleListDragEnd}
                  onDrop={(e) => handleListDrop(e, list.id)}
                >
                  {canDrag && (
                    <div
                      className="todo-drag-handle"
                      style={{ marginRight: '0.75rem', cursor: 'grab', flexShrink: 0 }}
                      aria-label="Reorder list"
                      title="Drag to reorder"
                    >
                      ⋮⋮
                    </div>
                  )}
                  <div className="account-list-info">
                  <div className="account-list-header">
                    {isEditing ? (
                      <div className="account-list-edit-container">
                        <input
                          type="text"
                          value={editingListName}
                          onChange={(e) => setEditingListName(e.target.value)}
                          onKeyDown={(e) => handleEditKeyDown(e, list.id)}
                          className="account-list-edit-input"
                          autoFocus
                          aria-label="Edit list name"
                        />
                        <div className="account-list-edit-actions">
                          <button
                            className="btn-edit-save"
                            onClick={() => handleEditSave(list.id)}
                            aria-label="Save list name"
                          >
                            <svg width="16" height="16" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2">
                              <path d="M16 4l-8 8-4-4" />
                            </svg>
                            <span className="btn-edit-text">Save</span>
                          </button>
                          <button
                            className="btn-edit-cancel"
                            onClick={handleEditCancel}
                            aria-label="Cancel editing"
                          >
                            <svg width="16" height="16" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2">
                              <path d="M4 4l12 12M16 4l-12 12" />
                            </svg>
                            <span className="btn-edit-text">Cancel</span>
                          </button>
                        </div>
                      </div>
                    ) : (
                      <h3 className="account-list-name">
                        {displayName}
                      </h3>
                    )}
                  </div>
                  <div className="account-list-stats">
                    <span className="account-list-stat">
                      {itemCount} {itemCount === 1 ? 'item' : 'items'}
                    </span>
                    <span className="account-list-stat">
                      {openCount} open
                    </span>
                    <span className="account-list-stat">
                      {completedCount} completed
                    </span>
                  </div>
                  </div>
                {!isEditing && (
                  <div className="account-list-actions">
                    {!isMain && !isTomorrow && (
                      <>
                        {!isDailyHabits && (
                          <button
                            className="btn-edit-list"
                            onClick={() => handleEditClick(list)}
                            aria-label={`Edit ${displayName}`}
                            data-tooltip="Edit"
                          >
                            <svg width="18" height="18" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2">
                              <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                              <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                            </svg>
                            <span className="btn-text-mobile">Edit</span>
                          </button>
                        )}
                        <button
                          className="btn-delete-list"
                          onClick={() => handleDeleteClick(list)}
                          aria-label={`Delete ${displayName}`}
                          data-tooltip="Delete"
                        >
                          <svg width="18" height="18" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M3 6h14M8 6V4a2 2 0 0 1 2-2h0a2 2 0 0 1 2 2v2m3 0v10a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6h14zM10 11v6M7 11v6M13 11v6" />
                          </svg>
                          <span className="btn-text-mobile">Delete</span>
                        </button>
                      </>
                    )}
                  </div>
                )}
                </div>
              </React.Fragment>
            );
          })}
        </div>
      )}

      <ConfirmModal
        isOpen={deleteModal.isOpen}
        title={deleteModal.list?.shared_users?.length > 1 ? "Leave List" : "Delete List"}
        message={
          deleteModal.list?.shared_users?.length > 1
            ? `Are you sure you want to leave "${deleteModal.list?.name}"? You will lose access to this list and its ${deleteModal.list?.tasks?.length || 0} tasks.`
            : `Are you sure you want to delete "${deleteModal.list?.name}"? This will permanently delete the list and all ${deleteModal.list?.tasks?.length || 0} tasks associated with it.`
        }
        confirmText={deleteModal.list?.shared_users?.length > 1 ? "Leave" : "Delete"}
        cancelText="Cancel"
        onConfirm={handleDeleteConfirm}
        onCancel={handleDeleteCancel}
        isDanger={true}
      />
    </>
  );
}

